import { z } from 'zod'

/**
 * Staff access is role-based: a person holds a role, and the role holds permissions.
 * Nobody is granted a permission directly. The `admin` role is built in: it has every permission
 * below plus managing team access and roles, which cannot be granted to any other role.
 */
export const PERMISSIONS = [
  { key: 'clients.create', label: 'Create clients', group: 'Clients' },
  { key: 'clients.edit', label: 'Edit clients', hint: 'Compose portals, prefill answers, review submissions.', group: 'Clients' },
  { key: 'clients.delete', label: 'Delete clients and onboardings', hint: 'No delete action exists in the portal yet; this reserves it.', group: 'Clients' },
  { key: 'clients.invite', label: 'Send and revoke client invites', group: 'Clients' },
  { key: 'clients.members', label: 'Remove people from a client account', group: 'Clients' },
  { key: 'credentials.reveal', label: 'Reveal stored account logins', hint: 'Every reveal is written to the audit log.', group: 'Account credentials' },
  { key: 'credentials.manage', label: 'Add, replace or remove stored logins', group: 'Account credentials' },
  { key: 'data.import', label: 'Import client files', group: 'Data' },
  { key: 'data.export', label: 'Export client data', group: 'Data' },
  { key: 'directory.manage', label: 'Edit the team directory', group: 'Agency' },
  { key: 'settings.manage', label: 'Edit agency settings and the FAQ catalog', group: 'Agency' },
  { key: 'audit.view', label: 'View the audit log', group: 'Agency' },
] as const satisfies ReadonlyArray<{ key: string; label: string; hint?: string; group: string }>

export type Permission = (typeof PERMISSIONS)[number]['key']
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key) as Permission[]

export const ADMIN_ROLE_ID = 'admin'

export const RoleDoc = z.object({
  name: z.string().min(1).max(60),
  permissions: z.array(z.enum(PERMISSION_KEYS as [Permission, ...Permission[]])).default([]),
  updatedAt: z.number().optional(),
  updatedBy: z.string().optional(),
})
export type RoleDoc = z.infer<typeof RoleDoc>

/** staffAccess/{lowercased email}: who may use the team console, and with which role. */
export const StaffAccessDoc = z.object({
  email: z.string().email(),
  name: z.string().default(''),
  roleId: z.string().min(1),
  /** Set by the sign-in function for OWNER_EMAILS; owners are always admins and cannot be removed. */
  owner: z.boolean().optional(),
  addedBy: z.string().optional(),
  addedAt: z.number().optional(),
  /** When the portal invite email was last sent. */
  inviteSentAt: z.number().optional(),
  /** The built-in test team member (TEST_ACCOUNTS); left off the Team page. */
  test: z.boolean().optional(),
})
export type StaffAccessDoc = z.infer<typeof StaffAccessDoc>

/** Roles created on first seed. Editable afterwards in the console; the seed never overwrites them. */
export const DEFAULT_ROLES: Record<string, Pick<RoleDoc, 'name' | 'permissions'>> = {
  manager: {
    name: 'Manager',
    permissions: ['clients.create', 'clients.edit', 'clients.invite', 'credentials.reveal', 'credentials.manage', 'data.export'],
  },
}

export function roleHas(roleId: string | null | undefined, role: Pick<RoleDoc, 'permissions'> | null | undefined, p: Permission): boolean {
  if (roleId === ADMIN_ROLE_ID) return true
  return Boolean(role?.permissions.includes(p))
}

/**
 * Built-in accounts admins can "Sign in as" to try the portal as a client or a team member. The server creates them on
 * first use. Their addresses are on the reserved .test domain, so nothing can ever be emailed to them.
 */
export const TEST_CLIENT_ID = 'sonder-test-client'
export const TEST_ACCOUNTS = {
  client: { email: 'test-client@sonder-portal.test', name: 'Test Client', label: 'Manager at Test Client' },
  team: { email: 'test-team@sonder-portal.test', name: 'Test Team Member', label: 'Team member (Manager role)' },
} as const
export type TestAccount = keyof typeof TEST_ACCOUNTS
